import { Component, OnInit, ViewEncapsulation, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { ConfigManagerService } from '@xpo-ltl/config-manager';
import { XpoAuthenticationService } from '@xpo-ltl/ngx-auth';
import { XpoConfirmDialog, XpoConfirmDialogConfig } from '@xpo-ltl/ngx-ltl-core/confirm-dialog';
import { XpoShellRoute } from '@xpo-ltl/ngx-ltl-core/shell';
import { User } from '@xpo-ltl/sdk-common';
import { invoke as _invoke, isEmpty as _isEmpty, debounce } from 'lodash';
import { BehaviorSubject, Subject, interval } from 'rxjs';
import { delay, distinctUntilChanged, filter, retryWhen, skipWhile, take, tap } from 'rxjs/operators';
import { AppRoutes } from './shared/enums/app-routes.enum';
import { AppTitle } from './shared/enums/app-title.enum';
import { ConfigManagerProperties } from './shared/enums/config-manager-properties.enum';
import { UserPermission } from './shared/guards/permission/permission.enum';
import { UserPermissionService } from './shared/services/user/user-permission.services';
import { UserService } from './shared/services/user/user.services';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { TariffManagementApiService } from '@xpo-ltl/sdk-tariffmanagement';

interface XpoShellRouteSecure extends XpoShellRoute {
  permission?: UserPermission;
}
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  userActivity;
  userInactive: Subject<any> = new Subject();
  userIsActive: boolean;
  title: string;
  build: string;
  routes: Array<any> = [
    {
      label: 'Tariff List',
      path: `/${AppRoutes.TARIFF_LIST}`,
      permission: UserPermission.tariffList,
    },
    {
      label: 'Get Rate Blocks',
      path: `/${AppRoutes.TARIFF_DETAIL}`,
      permission: UserPermission.tariffDetail,
    },
    {
      label: 'Transit Time File Request',
      path: `/${AppRoutes.TRANSIT_TIME_FILE_REQUEST}`,
      permission: UserPermission.transitTimeFile,
    },
    {
      label: 'Rate File Request',
      path: `/${AppRoutes.RATE_FILE_REQUEST}/${AppRoutes.FILE}`,
      permission: UserPermission.rateFile,
    },
  ];
  email: string = 'ITFreightPricingRatingSupport@xpo.com';

  userRoutes = new BehaviorSubject<XpoShellRoute[]>([]);
  userRoutes$ = this.userRoutes.asObservable();
  private readonly currentUserSubject = new BehaviorSubject<User>(undefined);
  private hasNavigatedToDefaultRoute = false;

  constructor(
    private configManagerService: ConfigManagerService,
    private authService: XpoAuthenticationService,
    private tariffManagementApiService: TariffManagementApiService,
    private userService: UserService,
    private userPermissionService: UserPermissionService,
    private confirmDialog: XpoConfirmDialog,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.title = AppTitle.tariffManagement;
    this.build = configManagerService.getSetting<string>(ConfigManagerProperties.buildVersion);
  }

  ngOnInit(): void {
    this.handleLoggedInUser();
    this.debouncedRefreshUserState = this.debouncedRefreshUserState.bind(this);
  }

  private getUserRoutes(routes: XpoShellRouteSecure[], permissions: Array<string>): XpoShellRoute[] {
    const result = routes
      .filter((route: XpoShellRouteSecure) => {
        return !route.permission || permissions.includes(route.permission) ? true : false;
      })
      .map((route: XpoShellRouteSecure) => {
        return {
          label: route.label,
          path: route.path,
        };
      });

    return result;
  }

  setSessionTimeOut() {
    this.userActivity = setTimeout(() => this.userInactive.next(undefined), 900000);
  }

  @HostListener('window:mousemove')
  @HostListener('window:keydown')
  debouncedRefreshUserState = debounce(() => {
    clearTimeout(this.userActivity);
    this.setSessionTimeOut();
  }, 500);

  logOut() {
    this.authService.signOut();
    this.router.navigate([`/postsignout`]);
    this.dialog.closeAll();
  }

  openSessionDialog() {
    this.userIsActive = false;
    setTimeout(() => {
      if (!this.userIsActive) {
        this.logOut();
      }
    }, 200000);
    const confirmConfig: XpoConfirmDialogConfig = {
      confirmButtonText: 'Yes',
      icon: 'warning',
      rejectButtonText: '',
    };
    this.confirmDialog.confirm('', 'Are you still there?', confirmConfig).subscribe((resp) => {
      this.userIsActive = true;
    });
  }

  verifyPermissions(): void {
    this.userPermissionService.userPermissions$
      .pipe(
        filter((permissions) => permissions !== undefined),
        distinctUntilChanged((previousPermissions, currentPermissions) => {
          return (
            previousPermissions.length === currentPermissions.length &&
            previousPermissions.every((permission, index) => permission === currentPermissions[index])
          );
        })
      )
      .subscribe((permissions) => {
        const hasPermissions = permissions.length > 0;
        if (hasPermissions) {
          const permissionsBasedRoutes = this.getUserRoutes(this.routes, permissions);
          this.userRoutes.next(permissionsBasedRoutes);
          if (!this.hasNavigatedToDefaultRoute && this.router.url === AppRoutes.ROOT) {
            this.hasNavigatedToDefaultRoute = true;
            this.userPermissionService.navigateToDefaultRoute(permissions);
          }
        } else {
          const isAlreadyOnNotAuthorized = this.router.url?.startsWith(`/${AppRoutes.NOT_AUTHORIZED_PAGE}`);
          if (!isAlreadyOnNotAuthorized) {
            this.router.navigate([AppRoutes.NOT_AUTHORIZED_PAGE]);
          }
        }
      });
  }

  private handleLoggedInUser(): void {
    this.tariffManagementApiService
      .loggedInUser()
      .pipe(
        retryWhen((errors) => {
          return errors.pipe(delay(1000), take(5));
        })
      )
      .subscribe(
        (user: User) => {
          if (user) {
            this.userService.userSource.next(user);
            this.userPermissionService.userLoaded$.next(true);
            this.verifyPermissions();
            this.setDynatraceUserIdentity(user);
            this.setSessionTimeOut();
            this.userInactive.subscribe(() => this.openSessionDialog());
          }
        },
        (error) => {}
      );
  }

  private setDynatraceUserIdentity(user: User): void {
    const setUser = (): void =>
      _invoke(window['dtrum'], 'identifyUser', !_isEmpty(user.emailAddress) ? user.emailAddress : user.userId);
    if ((window['dtrum'] || {}).identifyUser) {
      setUser();
    } else {
      let retryCount: number = 0;
      interval(1000)
        .pipe(
          tap(() => retryCount++),
          skipWhile(() => !(window['dtrum'] || {}).identifyUser && retryCount <= 60),
          take(1)
        )
        .subscribe(() => {
          setUser();
        });
    }
  }
}
